import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import LoadingSpinner from '../../components/LoadingSpinner';
import { trackError, createErrorMeta } from '../../lib/telemetry';

interface MetricsData {
  date: string;
  totalUsers: number;
  totalViews: number;
  totalActions: number;
  totalDwellMs: number;
  totalUploadBytes: number;
  deviceBreakdown: Record<string, number>;
  planBreakdown: Record<string, number>;
  topPages: Record<string, number>;
  topActions: Record<string, number>;
  processedAt: string;
}

const Metrics: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });

  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildMetrics = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const buildDailyMetricsFn = httpsCallable<{ date?: string }, MetricsData>(
        functions,
        'buildDailyMetrics'
      );

      const result = await buildDailyMetricsFn({ date: selectedDate.replace(/-/g, '') });
      setMetrics(result.data);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to build metrics';
      setError(errorMessage);

      trackError(
        'Metrics build failed',
        createErrorMeta('ui', 'METRICS_BUILD_FAIL', 'S3', 'degraded', 'next-release', {
          userAction: 'build_metrics',
        }),
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Usage Metrics</h1>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-end space-x-4">
          <div className="flex-1 max-w-xs">
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <input
              type="date"
              id="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={buildMetrics}
            disabled={isLoading}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Building...' : 'Build Metrics'}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow p-8">
          <LoadingSpinner size="lg" text="Building metrics..." />
        </div>
      )}

      {/* Metrics Display */}
      {metrics && !isLoading && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Total Users
              </h3>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {metrics.totalUsers.toLocaleString()}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Page Views
              </h3>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {metrics.totalViews.toLocaleString()}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Actions</h3>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {metrics.totalActions.toLocaleString()}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Upload Size
              </h3>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {formatBytes(metrics.totalUploadBytes)}
              </p>
            </div>
          </div>

          {/* Detailed Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Device Breakdown */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Device Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(metrics.deviceBreakdown).map(([device, count]) => (
                  <div key={device} className="flex justify-between items-center">
                    <span className="text-gray-600">{device}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Plan Breakdown */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Plan Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(metrics.planBreakdown).map(([plan, count]) => (
                  <div key={plan} className="flex justify-between items-center">
                    <span className="text-gray-600">{plan}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Pages */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Pages</h3>
              <div className="space-y-2">
                {Object.entries(metrics.topPages)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([page, count]) => (
                    <div key={page} className="flex justify-between items-center">
                      <span className="text-gray-600 truncate mr-2">{page}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Top Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Actions</h3>
              <div className="space-y-2">
                {Object.entries(metrics.topActions)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([action, count]) => (
                    <div key={action} className="flex justify-between items-center">
                      <span className="text-gray-600 truncate mr-2">{action}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">
              <p>
                <strong>Total Dwell Time:</strong> {formatDuration(metrics.totalDwellMs)}
              </p>
              <p>
                <strong>Processed At:</strong> {new Date(metrics.processedAt).toLocaleString()}
              </p>
              <p>
                <strong>Date:</strong> {metrics.date}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Metrics;
